<template>
  <div class="chart-root">
    <div
      class="chart-wrapper"
      :style="{
        height: chartHeight + 'px',
      }"
    >
      <area-chart
        ref="chart"
        type="area"
        :height="chartHeight"
        :width="widget.size.w - 20"
        :options="chartOptions"
        :series="series"
      />
    </div>

    <!-- ปุ่มด้านล่าง -->
    <div class="chart-toolbar">
      <v-btn small @click="resetData">NOW</v-btn>
      <v-btn small @click="loadLog(120, 1)">1H</v-btn>
      <v-btn small @click="loadLog(2880, 2)">1D</v-btn>
      <v-btn small @click="loadLog(20160, 3)">7D</v-btn>

      <v-spacer></v-spacer>

      <v-btn small text @click="exportToCSV">
        <v-icon left small>mdi-download</v-icon>
        EXPORT CSV
      </v-btn>
    </div>
  </div>
</template>

<script>
import AreaChart from "vue-apexcharts";
import axios from "axios";
import { nextTick } from "vue";

export default {
  components: { AreaChart },
  props: ["widget"],
  data() {
    return {
      series: [{ name: this.widget.name, data: [] }],
      chartOptions: {
        chart: { type: "area" },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth" },
        grid: {
          borderColor: "#eee",
          strokeDashArray: 3,
          padding: {
            bottom: 15, // ดัน label แกน X ขึ้นมาจากขอบล่างอีกนิด
          },
        },
        xaxis: {
          type: "datetime",
          categories: [],
          labels: { datetimeUTC: true },
        },
        tooltip: {
          x: { format: "dd/MM/yy HH:mm" },
        },
        colors: [this.widget.params.Color],
      },
      point: 10,
      mode: 0,
    };
  },
  computed: {
    // พื้นที่ให้กราฟ = ความสูง widget - ส่วนหัวของ widget (ชื่อ + เวลา) - toolbar ปุ่ม
    chartHeight() {
      // เผื่อพื้นที่หัว widget + toolbar + ช่องว่างเพิ่ม
      const header = 60; // ชื่อ + เวลา ที่อยู่บน widget (จาก WidgetController)
      const toolbar = 20; // แถวปุ่ม NOW / 1H / 1D / 7D / EXPORT CSV
      const padding = 10; // เผื่อช่องว่างอีกนิด กันวันที่ชิดขอบ

      return Math.max(
        this.widget.size.h - (header + toolbar + padding),
        120 // อย่างน้อยให้กราฟสูงพอ
      );
    },
  },
  methods: {
    resetData() {
      this.series[0].data = [];
      this.chartOptions.xaxis.categories = [];
      this.point = 20;
      this.mode = 0;
      this.update();
    },
    async update() {
      this.series[0].data.unshift(this.widget.payload);
      this.series[0].data = this.series[0].data.slice(0, this.point);
      this.chartOptions.xaxis.categories.unshift(
        new Date(this.widget.timestamp).getTime() + 25200000
      );
      this.chartOptions.xaxis.categories =
        this.chartOptions.xaxis.categories.slice(0, this.point);
      this.updateChart();
    },
    async loadLog(point, mode) {
      this.point = point;
      this.mode = mode;
      try {
        const res = await axios.get(
          this.$store.state.serverUrl + "getAll/" + this.widget.topic
        );
        const data = res.data || [];

        if (mode === 1 || mode === 2) {
          const sliced = data.slice(0, this.point);
          this.series[0].data = sliced.map((d) => d.p);
          this.chartOptions.xaxis.categories = sliced.map(
            (d) => new Date(d.t).getTime() + 25200000
          );
        } else if (mode === 3) {
          const loop = Math.min(data.length, this.point / 60);
          this.series[0].data = Array.from(
            { length: loop },
            (_, i) => data[i * 60].p
          );
          this.chartOptions.xaxis.categories = Array.from(
            { length: loop },
            (_, i) => new Date(data[i * 60].t).getTime() + 25200000
          );
        }
        this.updateChart();
      } catch (e) {
        console.error(e);
      }
    },
    updateChart() {
      nextTick(() => {
        if (this.$refs.chart) {
          this.$refs.chart.updateOptions(this.chartOptions);
          this.$refs.chart.updateSeries(this.series);
        }
      });
    },
    async exportToCSV() {
      await this.loadLog(20160, 3);
      const rows = [["Timestamp", "Value"]];
      for (let i = 0; i < this.series[0].data.length; i++) {
        rows.push([
          new Date(this.chartOptions.xaxis.categories[i]).toISOString(),
          this.series[0].data[i],
        ]);
      }
      const csv = rows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "7-day-data.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
  mounted() {
    this.$store.watch(
      (state) => state.devices.find((d) => d.topic === this.widget.topic),
      (device) => {
        if (!device) return;
        this.widget.payload = device.payload;
        this.series[0].name = this.widget.params.name;
        this.chartOptions.colors[0] = this.widget.params.Color;
        if (this.mode === 0) this.update();
      },
      { deep: true }
    );
  },
};
</script>

<style scoped>
.chart-root {
  display: flex;
  flex-direction: column;
}

.chart-wrapper {
  padding: 10px;
  overflow: hidden; /* กันไม่ให้กราฟล้นออกจากโซนของมัน */
}

.chart-toolbar {
  display: flex;
  align-items: center;
  padding: 0 10px 5px 10px;
}
</style>
